import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listParameters, deleteParameter } from '../services/parameterService';
import type { Parameter } from '../types';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Modal } from '../components/ui/Modal';
import ParameterTestDialog from './ParameterTestDialog';

const PAGE_SIZE = 100;

const typeFilterOptions = [
  { value: '', label: 'All Types' },
  { value: 'INPUT', label: 'Input' },
  { value: 'CALCULATED', label: 'Calculated' },
];

export default function ParameterListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [parameters, setParameters] = useState<Parameter[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Parameter | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [testParam, setTestParam] = useState<Parameter | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchParameters = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listParameters({
        type: typeFilter || undefined,
        search: debouncedSearch || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setParameters(result.parameters);
      setTotal(result.total);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load parameters';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [typeFilter, debouncedSearch, page]);

  useEffect(() => {
    fetchParameters();
  }, [fetchParameters]);

  const handleFilterChange = (value: string) => {
    setTypeFilter(value);
    setPage(1);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setIsDeleting(true);
    try {
      await deleteParameter(deleteConfirm.parameterId);
      setSuccess(`Parameter "${deleteConfirm.parameterName}" deleted successfully.`);
      setDeleteConfirm(null);
      fetchParameters();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { error?: string } } };
      if (axiosErr.response?.status === 409) {
        setError(axiosErr.response.data?.error || 'Cannot delete parameter: it is used in active agreements.');
      } else {
        setError('Failed to delete parameter.');
      }
      setDeleteConfirm(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="parameter-list-page">
      <div className="page-header">
        <h2>Parameter Management</h2>
        {isAdmin && (
          <Button onClick={() => navigate('/parameters/create')}>
            Create Parameter
          </Button>
        )}
      </div>

      <div className="filters-row">
        <Select
          label="Parameter Type"
          options={typeFilterOptions}
          value={typeFilter}
          onChange={(e) => handleFilterChange(e.target.value)}
          aria-label="Filter by parameter type"
        />
        <Input
          placeholder="Search by name..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search parameters by name"
        />
      </div>

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" onDismiss={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {isLoading ? (
        <LoadingSpinner message="Loading parameters..." />
      ) : (
        <>
          <div className="table-wrapper">
            <table className="data-table" role="table">
              <thead>
                <tr>
                  <th scope="col">Parameter Name</th>
                  <th scope="col">Type</th>
                  <th scope="col">Source</th>
                  <th scope="col">Data Type</th>
                  <th scope="col">Display Order</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {parameters.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty-row">
                      No parameters found.
                    </td>
                  </tr>
                ) : (
                  parameters.map((param) => (
                    <tr key={param.parameterId}>
                      <td>{param.parameterName}</td>
                      <td>
                        <span className={`status-badge ${param.parameterType === 'INPUT' ? 'status-active' : 'status-badge-calculated'}`}>
                          {param.parameterType}
                        </span>
                      </td>
                      <td>
                        {param.parameterType === 'INPUT' ? (
                          (param.sourceType === 'SYSTEM' || !param.sourceType) ? (
                            <span className="status-badge status-badge-system" title="From ATS database (read-only)">
                              SYSTEM
                            </span>
                          ) : (
                            <span className="status-badge status-badge-manual" title="Operator enters value">
                              MANUAL
                            </span>
                          )
                        ) : '—'}
                      </td>
                      <td>{param.dataType || '—'}</td>
                      <td>{param.displayOrder}</td>
                      <td className="actions-cell">
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/parameters/${param.parameterId}/edit`)}
                          >
                            Edit
                          </Button>
                        )}
                        {isAdmin && param.sourceType === 'MANUAL' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(param)}
                          >
                            Delete
                          </Button>
                        )}
                        {param.parameterType === 'CALCULATED' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setTestParam(param)}
                          >
                            Test
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/parameters/${param.parameterId}/history`)}
                        >
                          History
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination" role="navigation" aria-label="Pagination">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="pagination-info">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Delete confirmation modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        size="sm"
      >
        <p>Are you sure you want to delete parameter "{deleteConfirm?.parameterName}"?</p>
        <div className="form-actions" style={{ marginTop: '1rem' }}>
          <Button variant="danger" onClick={handleDelete} isLoading={isDeleting} disabled={isDeleting}>
            Delete
          </Button>
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)} disabled={isDeleting}>
            Cancel
          </Button>
        </div>
      </Modal>

      {/* Test formula dialog */}
      {testParam && (
        <ParameterTestDialog
          isOpen={!!testParam}
          onClose={() => setTestParam(null)}
          formula={testParam.naturalLanguageFormula || ''}
        />
      )}
    </div>
  );
}
